import {connect} from 'react-redux';

import cloneDeep from 'lodash/cloneDeep';

import {Assignables} from 'nutshell-core/assignables';
import {SavedFilters} from 'nutshell-core/saved-filters';
import type {SavedFilter, SavedReportType, SavedReportId} from 'nutshell-core/types';

import type {NutshellState} from '../../store';

import type {SavedFilterHumanReadableType} from '../base/title-bar/title-bar-save-controls';
import {TitleBarSaveControlsContainer} from '../base/title-bar/title-bar-save-controls-container';
import type {ParentType} from '../base/title-bar/title-bar-save-controls-container';

import {getReportSaveType} from './report-constants';
import type {ReportKey, ReportURLParams} from './report-types';
import {getSafeUrlParams} from './report-utils';

type OwnProps = {
    hasSaveControls?: boolean;
    canResave: boolean;
    savedFilterHumanReadableType: SavedFilterHumanReadableType;
    onSavedFiltersButtonClick: () => any;
    reportKey: ReportKey;
    urlParams: ReportURLParams<'leads'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    parentType: ParentType;
};
type StateProps = {
    activeSavedFilter: SavedFilter<SavedReportType> | null | undefined;
    usersAndTeams: Array<any>;
    isResaving: boolean;
    isSavingNew: boolean;
    isSavingSharePreferences: boolean;
    errorText: string | null | undefined;
};
type DispatchProps = {
    onDismissPopoverError: () => any;
    onUpdateSharePreferences: (
        id: string,
        params: {
            isShared: boolean;
            links: any;
        }
    ) => Promise<any>;
    onResaveFilter: (id: string) => any;
    onSaveNewFilter: (arg1: string) => any;
};
type Props = OwnProps & StateProps & DispatchProps;

// @ts-expect-error
function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
    return {
        onDismissPopoverError: () => {
            dispatch(SavedFilters.clearActionError());
        },
        onUpdateSharePreferences: (id, params) => {
            return dispatch(SavedFilters.updateSharePreferences(id, params));
        },
        onResaveFilter: (id: string) => {
            const value = getSafeUrlParams(ownProps.urlParams);
            const params = {value} as const;
            dispatch(SavedFilters.resaveFilter(id, params));
        },
        onSaveNewFilter: (name: string) => {
            const currentFilterId = ownProps.urlParams.report ? ownProps.urlParams.report.id : null;

            // Dispatch a thunk that checks if current filter is hidden
            // @ts-expect-error
            return dispatch((innerDispatch, getState) => {
                const state = getState();
                const savedFilters = SavedFilters.getSavedFilters(state);
                const currentFilter = currentFilterId ? savedFilters[currentFilterId] : null;

                // If current filter is hidden (AI-generated), unhide it
                if (currentFilter && currentFilter.isHidden && currentFilterId) {
                    return innerDispatch(SavedFilters.unhideFilter(currentFilterId, name));
                }

                // Otherwise, create a new saved filter
                const reportType = getReportSaveType(ownProps.reportKey);
                const value = getSafeUrlParams(ownProps.urlParams);

                return innerDispatch(SavedFilters.saveNewFilter(name, reportType, value, false));
            }).then((savedReportId: SavedReportId) => {
                const newParams = cloneDeep(ownProps.urlParams);
                newParams.report = {...newParams.report, id: savedReportId};
                ownProps.onParamChange(newParams, false);
            });
        },
    };
}

// @ts-expect-error
function mapStateToProps(state: NutshellState, ownProps): StateProps {
    const reportId: SavedReportId | null | undefined = ownProps.urlParams.report
        ? ownProps.urlParams.report.id
        : undefined;

    return {
        activeSavedFilter: reportId ? SavedFilters.getSavedReports(state)[reportId] : undefined,
        usersAndTeams: Assignables.getAll(state),
        isResaving: SavedFilters.getIsResaving(state),
        isSavingNew: SavedFilters.getIsSavingNew(state),
        isSavingSharePreferences: SavedFilters.getIsSavingSharePreferences(state),
        errorText: SavedFilters.getActionErrorMessage(state),
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const ReportTitleBarSaveControls = connector(TitleBarSaveControlsContainer);
